import { useCallback, useEffect, useState } from 'react';
import { Form, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { AssetService } from '~/app/services/asset/asset.service';
import { AssetTypeManufacturerModelCatalogApi } from '~/app/services/knowledgeBase/catalog.api';
import { assetTypesApi } from '~/app/services/knowledgeBase/api-instances';
import { OrganizationService } from '~/app/services/organization/organization.service';
import type {
  AssetTypeManufacturerModelCreateFormValues,
  SelectOption,
} from '../types';

type UseAssetTypeManufacturerModelCreateModalParams = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

export function useAssetTypeManufacturerModelCreateModal({
  open,
  onClose,
  onSuccess,
}: UseAssetTypeManufacturerModelCreateModalParams) {
  const { t } = useTranslation('componentSchema');
  const [form] = Form.useForm<AssetTypeManufacturerModelCreateFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [assetTypeOptions, setAssetTypeOptions] = useState<SelectOption[]>([]);
  const [manufacturerOptions, setManufacturerOptions] = useState<
    SelectOption[]
  >([]);
  const [assetModelOptions, setAssetModelOptions] = useState<SelectOption[]>(
    [],
  );
  const [selectedAssetTypeId, setSelectedAssetTypeId] = useState<string | null>(
    null,
  );

  const resetState = useCallback(() => {
    form.resetFields();
    setSelectedAssetTypeId(null);
    setAssetModelOptions([]);
  }, [form]);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const loadOptions = async () => {
      setLoadingOptions(true);
      try {
        const [assetTypesRes, manufacturerOrgs] = await Promise.all([
          assetTypesApi.assetTypeControllerFindAll({ limit: 1000, page: 1 }),
          // Only organizations with organizationType === Manufacturer
          OrganizationService.findManufacturerOrganizations(),
        ]);

        if (cancelled) return;

        setAssetTypeOptions(
          (assetTypesRes.data ?? []).map((assetType) => ({
            value: assetType.id,
            label: `${assetType.code} — ${assetType.name}`,
          })),
        );
        setManufacturerOptions(
          manufacturerOrgs.map((organization) => ({
            value: organization.id,
            label: organization.name,
          })),
        );
      } catch {
        if (!cancelled) {
          message.error(t('atmmCreate.loadFailed'));
        }
      } finally {
        if (!cancelled) {
          setLoadingOptions(false);
        }
      }
    };

    void loadOptions();

    return () => {
      cancelled = true;
    };
  }, [open, t]);

  useEffect(() => {
    if (!open || !selectedAssetTypeId) {
      setAssetModelOptions([]);
      return;
    }

    let cancelled = false;

    const loadModels = async () => {
      setLoadingModels(true);
      try {
        const models =
          await AssetService.findAssetModelsByAssetTypeId(selectedAssetTypeId);
        if (cancelled) return;
        setAssetModelOptions(
          models.map((model) => ({
            value: model.id,
            label: model.name,
          })),
        );
      } catch {
        if (!cancelled) {
          message.error(t('atmmCreate.loadModelsFailed'));
          setAssetModelOptions([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingModels(false);
        }
      }
    };

    void loadModels();

    return () => {
      cancelled = true;
    };
  }, [open, selectedAssetTypeId, t]);

  const handleAssetTypeChange = useCallback(
    (assetTypeId: string) => {
      setSelectedAssetTypeId(assetTypeId);
      form.setFieldsValue({ assetModelId: undefined });
    },
    [form],
  );

  const handleCancel = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      await AssetTypeManufacturerModelCatalogApi.create({
        assetTypeId: values.assetTypeId,
        manufacturerId: values.manufacturerId,
        assetModelId: values.assetModelId,
      });
      message.success(t('atmmCreate.success'));
      resetState();
      onSuccess();
      onClose();
    } catch (error) {
      if (error && typeof error === 'object' && 'errorFields' in error) {
        return;
      }
      const rawMessage =
        error instanceof Error ? error.message : t('atmmCreate.failed');
      const isDuplicate =
        rawMessage.includes('already exists') ||
        rawMessage.includes('existe déjà');
      message.error(isDuplicate ? t('atmmCreate.duplicate') : rawMessage);
    } finally {
      setSubmitting(false);
    }
  }, [form, onClose, onSuccess, resetState, t]);

  return {
    form,
    submitting,
    loadingOptions,
    loadingModels,
    assetTypeOptions,
    manufacturerOptions,
    assetModelOptions,
    selectedAssetTypeId,
    handleAssetTypeChange,
    handleCancel,
    handleSubmit,
  };
}
